import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface TermsProps {
  onClose: () => void;
  onAgree: () => void;
}

interface Term {
  id: number;
  title: string;
  content: string;
  version: string;
}

export default function TermsAgreement({ onClose, onAgree }: TermsProps) {
  const [terms, setTerms] = useState<Term[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [allChecked, setAllChecked] = useState(false);
  const [checks, setChecks] = useState<boolean[]>([]);

  useEffect(() => {
    const fetchTerms = async () => {
      const { data } = await supabase
        .from('terms')
        .select('id, title, content, version')
        .eq('is_active', true)
        .order('id');

      if (data) {
        setTerms(data);
        setChecks(new Array(data.length).fill(false));
      }
    };
    fetchTerms();
  }, []);

  const handleAllCheck = () => {
    const newVal = !allChecked;
    setAllChecked(newVal);
    setChecks(new Array(terms.length).fill(newVal));
  };

  const handleSingleCheck = (idx: number) => {
    const newChecks = [...checks];
    newChecks[idx] = !newChecks[idx];
    setChecks(newChecks);
    setAllChecked(newChecks.every(Boolean));
  };

  const allRequiredChecked = checks.every(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-lg max-h-[80vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h2 className="font-heading text-lg text-foreground-800">약관 동의</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-background-200 px-5">
          {terms.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === i
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-foreground-400 hover:text-foreground-600'
              }`}
            >
              {t.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {terms[activeTab] && (
            <div>
              <p className="text-xs text-foreground-300 mb-3">버전: {terms[activeTab].version}</p>
              <div className="text-sm text-foreground-600 whitespace-pre-line leading-relaxed">
                {terms[activeTab].content}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-background-200 space-y-3">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <button
              type="button"
              onClick={handleAllCheck}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                allChecked ? 'bg-primary-500 border-primary-500' : 'border-background-300'
              }`}
            >
              {allChecked && <i className="ri-check-line text-white text-xs"></i>}
            </button>
            <span className="text-sm text-foreground-700">전체 동의</span>
          </label>

          {terms.map((t, i) => (
            <label key={t.id} className="flex items-center gap-2.5 cursor-pointer ml-7">
              <button
                type="button"
                onClick={() => handleSingleCheck(i)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                  checks[i] ? 'bg-primary-500 border-primary-500' : 'border-background-300'
                }`}
              >
                {checks[i] && <i className="ri-check-line text-white text-xs"></i>}
              </button>
              <span className="text-sm text-foreground-500">{t.title} (필수)</span>
            </label>
          ))}

          <button
            onClick={() => {
              if (allRequiredChecked) {
                onAgree();
              }
            }}
            disabled={!allRequiredChecked}
            className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            동의하고 계속하기
          </button>
        </div>
      </div>
    </div>
  );
}